import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

interface NotificationProps {
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
  onClose: () => void;
  autoClose?: number; // milliseconds
}

export const Notification: React.FC<NotificationProps> = ({
  type,
  title,
  message,
  onClose,
  autoClose = 5000
}) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(onClose, autoClose);
      return () => clearTimeout(timer);
    }
  }, [autoClose, onClose]);

  const bgColors = {
    success: 'bg-green-50 border-green-300',
    error: 'bg-red-50 border-red-300',
    info: 'bg-blue-50 border-blue-300',
    warning: 'bg-yellow-50 border-yellow-300'
  };

  const textColors = {
    success: 'text-green-800',
    error: 'text-red-800',
    info: 'text-blue-800',
    warning: 'text-yellow-800'
  };

  const icons = {
    success: { icon: faCheckCircle, color: 'text-green-600' },
    error: { icon: faExclamationTriangle, color: 'text-red-600' },
    info: { icon: faInfoCircle, color: 'text-blue-600' },
    warning: { icon: faExclamationTriangle, color: 'text-yellow-600' }
  };

  return (
    <>
      <style>{`
        @keyframes notification-slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes notification-shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        .notification-animate-slide-in {
          animation: notification-slide-in 0.3s ease-out;
        }
        .notification-progress-bar {
          animation: notification-shrink ${autoClose}ms linear;
        }
      `}</style>
      <div className={`fixed top-4 right-4 z-50 max-w-md w-full border-2 rounded-lg shadow-2xl ${bgColors[type]} notification-animate-slide-in`}>
        <div className="p-4">
          <div className="flex items-start">
            <FontAwesomeIcon
              icon={icons[type].icon}
              className={`text-xl ${icons[type].color} mr-3 mt-0.5`}
            />
            <div className="flex-1">
              <h3 className={`text-sm font-bold ${textColors[type]} mb-1`}>{title}</h3>
              {message && (
                <p className={`text-xs ${textColors[type]} opacity-90`}>{message}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className={`ml-4 ${textColors[type]} hover:opacity-70 transition-opacity`}
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm" />
            </button>
          </div>
        </div>
        {autoClose && (
          <div className="h-1 bg-gray-200 overflow-hidden">
            <div
              className={`h-full notification-progress-bar ${type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'}`}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Notification;
